import { MotionFlowEngine } from "./core/engine.js";
import { MotionFlowParallax } from "./core/parallax.js";
import { MotionFlowCount } from "./core/count.js";
import { MotionFlowRoller } from "./core/roller.js";
import { MotionFlowTicker } from "./core/ticker.js";
import { MotionFlowText } from "./core/text.js";

/* =====================================================
   INTERNAL INSTANCES
===================================================== */
let engineInstance   = null;
let parallaxInstance = null;
let countInstance    = null;
let rollerInstance   = null;
let tickerInstance   = null;
let textInstance     = null;

/* =====================================================
   INIT STATE
===================================================== */
let isInitialized = false;
let autoInitDone = false;
let manualInitCalled = false;
let lastInitOptions = {};

/* =====================================================
   INTERNAL DESTROY
===================================================== */
function destroyInstances({ destroyTicker = false } = {}) {
  engineInstance?.destroy?.();
  parallaxInstance?.destroy?.();
  countInstance?.destroy?.();
  rollerInstance?.destroy?.();
  textInstance?.destroy?.();

  if (destroyTicker) {
    tickerInstance?.destroy?.();
    tickerInstance = null;
  }

  engineInstance   = null;
  parallaxInstance = null;
  countInstance    = null;
  rollerInstance   = null;
  textInstance     = null;
}

/* =====================================================
   INIT (IDEMPOTENT, SILENT)
===================================================== */
function init(options = {}) {
  manualInitCalled = true;
  lastInitOptions = options;

  if (isInitialized) {
    destroyInstances({ destroyTicker: false });
  }

  /* -----------------------------
     TEXT
  ----------------------------- */
  textInstance = MotionFlowText(
    options.text === true ? {} : options.text || {}
  );

  /* -----------------------------
     SCROLL ENGINE
  ----------------------------- */
  engineInstance = MotionFlowEngine(options);

  /* -----------------------------
     SCROLL-BASED FEATURES
  ----------------------------- */
  parallaxInstance = MotionFlowParallax(options);
  countInstance    = MotionFlowCount(options);
  rollerInstance   = MotionFlowRoller(options);

  /* -----------------------------
     CONTINUOUS FEATURES
  ----------------------------- */
  tickerInstance = MotionFlowTicker(options);

  isInitialized = true;
  return MotionFlow;
}

/* =====================================================
   PUBLIC API
===================================================== */
const MotionFlow = {
  init,

  destroy() {
    destroyInstances({ destroyTicker: true });
    isInitialized = false;
  },

  /* ===============================================
     SCROLL
  =============================================== */
  scroll: {
    init() {
      if (!engineInstance) {
        engineInstance = MotionFlowEngine(lastInitOptions);
      }
    },
    refresh() {
      engineInstance?.refresh?.();
    },
    destroy() {
      engineInstance?.destroy?.();
      engineInstance = null;
    }
  },

  /* ===============================================
     PARALLAX
  =============================================== */
  parallax: {
    init() {
      if (!parallaxInstance) {
        parallaxInstance = MotionFlowParallax(lastInitOptions);
      }
    },
    refresh() {
      parallaxInstance?.refresh?.();
    },
    destroy() {
      parallaxInstance?.destroy?.();
      parallaxInstance = null;
    }
  },

  /* ===============================================
     COUNT
  =============================================== */
  count: {
    init() {
      if (!countInstance) {
        countInstance = MotionFlowCount(lastInitOptions);
      }
    },
    refresh() {
      countInstance?.refresh?.();
    },
    destroy() {
      countInstance?.destroy?.();
      countInstance = null;
    }
  },

  /* ===============================================
     ROLLER
  =============================================== */
  roller: {
    init() {
      if (!rollerInstance) {
        rollerInstance = MotionFlowRoller(lastInitOptions);
      }
    },
    refresh() {
      rollerInstance?.refresh?.();
    },
    destroy() {
      rollerInstance?.destroy?.();
      rollerInstance = null;
    }
  },

  /* ===============================================
     TICKER
  =============================================== */
  ticker: {
    init() {
      if (!tickerInstance) {
        tickerInstance = MotionFlowTicker(lastInitOptions);
      }
    },
    refresh() {
      tickerInstance?.refresh?.();
    },
    pause(target = null) {
      tickerInstance?.pause?.(target);
    },
    play(target = null) {
      tickerInstance?.play?.(target);
    },
    toggle(target = null) {
      tickerInstance?.toggle?.(target);
    },
    destroy() {
      tickerInstance?.destroy?.();
      tickerInstance = null;
    }
  },

  /* ===============================================
     TEXT
  =============================================== */
  text: {
    init() {
      if (!textInstance) {
        textInstance = MotionFlowText(
          lastInitOptions.text === true
            ? {}
            : lastInitOptions.text || {}
        );
      }
    },
    refresh() {
      textInstance?.refresh?.();
    },
    destroy() {
      textInstance?.destroy?.();
      textInstance = null;
    }
  }
};

export default MotionFlow;

/* =====================================================
   AUTO INIT (SAFE + CONFIG-FRIENDLY)
===================================================== */
if (typeof window !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => {
    if (manualInitCalled || autoInitDone) return;

    autoInitDone = true;
    MotionFlow.init();
  });
}
